import { useEffect, useRef, useCallback } from 'react';
var POINTER_START_EVENTS = ['mousedown', 'touchstart'];
var POINTER_MOVE_EVENTS = ['mousemove', 'touchmove'];
var POINTER_END_EVENTS = ['mouseup', 'touchend'];
var MOUSE_LEFT_KEY = 0;
var TouchEvent = window.TouchEvent;
function getClientCoords(e) {
    if (TouchEvent && e instanceof TouchEvent) {
        if (e.touches.length > 0) {
            return {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY,
            };
        }
        else if (e.changedTouches.length > 0) {
            return {
                clientX: e.changedTouches[0].clientX,
                clientY: e.changedTouches[0].clientY,
            };
        }
    }
    else if (e instanceof MouseEvent) {
        if (e.type !== 'mousedown') {
            e.preventDefault();
        }
        return { clientX: e.clientX, clientY: e.clientY };
    }
}
function on(element, events, handler) {
    events.forEach(function (event) {
        element.addEventListener(event, handler);
    });
}
function off(element, events, handler) {
    events.forEach(function (event) {
        element.removeEventListener(event, handler);
    });
}
var defaultRefFunction = function () {
    throw new Error('Cannot call an event handler while rendering.');
};
export function useEventCallback(fn, dependencies) {
    if (dependencies === void 0) { dependencies = []; }
    var ref = useRef(defaultRefFunction);
    useEffect(function () {
        ref.current = fn;
        // React's array spread caution =\
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fn].concat(dependencies));
    return useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var fn = ref.current;
        return fn.apply(void 0, args);
    }, [ref]);
}
export default function useDragged(elRef, onDrag) {
    var lastCoords = useRef(null);
    var firstCoords = useRef(null);
    var cb = useEventCallback(onDrag);
    useEffect(function () {
        var el = elRef.current;
        if (!el) {
            return;
        }
        el.style.touchAction = 'none';
        function onPointerMove(e) {
            if (lastCoords.current) {
                var _a = getClientCoords(e), clientX = _a.clientX, clientY = _a.clientY;
                var deltaX = clientX - lastCoords.current.x;
                var deltaY = clientY - lastCoords.current.y;
                var offsetX = clientX - firstCoords.current.x;
                var offsetY = clientY - firstCoords.current.y;
                cb({
                    el: el,
                    first: false,
                    last: false,
                    deltaX: deltaX,
                    deltaY: deltaY,
                    offsetX: offsetX,
                    offsetY: offsetY,
                    clientX: clientX,
                    clientY: clientY,
                });
                lastCoords.current = {
                    x: clientX,
                    y: clientY,
                };
            }
        }
        function onPointerEnd(e) {
            var _a = getClientCoords(e), clientX = _a.clientX, clientY = _a.clientY;
            lastCoords.current = null;
            cb({
                el: el,
                first: false,
                last: true,
                deltaX: 0,
                deltaY: 0,
                offsetX: 0,
                offsetY: 0,
                clientX: clientX,
                clientY: clientY,
            });
            off(document.documentElement, POINTER_END_EVENTS, onPointerEnd);
            off(document.documentElement, POINTER_MOVE_EVENTS, onPointerMove);
        }
        function onPointerStart(e) {
            if (e instanceof MouseEvent && e.button !== MOUSE_LEFT_KEY) {
                return;
            }
            e.stopPropagation();
            var _a = getClientCoords(e), clientX = _a.clientX, clientY = _a.clientY;
            firstCoords.current = {
                x: clientX,
                y: clientY,
            };
            lastCoords.current = firstCoords.current;
            cb({
                el: el,
                first: true,
                last: false,
                deltaX: 0,
                deltaY: 0,
                offsetX: 0,
                offsetY: 0,
                clientX: clientX,
                clientY: clientY,
            });
            on(document.documentElement, POINTER_END_EVENTS, onPointerEnd);
            on(document.documentElement, POINTER_MOVE_EVENTS, onPointerMove);
        }
        on(el, POINTER_START_EVENTS, onPointerStart);
        return function () {
            off(el, POINTER_START_EVENTS, onPointerStart);
            off(document.documentElement, POINTER_END_EVENTS, onPointerEnd);
            off(document.documentElement, POINTER_MOVE_EVENTS, onPointerMove);
            el.style.touchAction = null;
        };
    }, [elRef, cb]);
}
//# sourceMappingURL=index.js.map